import React, {useMemo, useState} from 'react'
import {useNavigate, useParams} from "react-router-dom";
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import {useQuery} from "react-query";
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    CircularProgress,
    TextField
} from "@mui/material";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import Button from "@mui/material/Button";
import {
    ReservationService
} from "crqlar-react-common/dist/services/reservation/reservation.service";
import GenericPrintoutPreview from './GenericPrintoutPreview';
import {useProfile} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {
    FolioItemService
} from "crqlar-react-common/dist/services/billing/folioItem.service";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {getSymbolForCurrency} from "crqlar-react-common/dist/utils/currency.util";
import {
    FolioPrintoutLine
} from "../../system/billing/folio/FolioPrintout.component";
import {
    usePrintTemplates
} from "crqlar-react-common/dist/utils/hooks/templates/usePrintTemplates";
import {
    PrintTemplate,
    PrintTemplateType
} from "crqlar-react-common/dist/models/template/printTemplate.model";
import {
    useLocalize,
    useTranslateForLanguage
} from "crqlar-react-common/dist/utils/text.util";

enum LanguageMode {
    ENGLISH = 'ENGLISH',
    GERMAN = 'GERMAN',
    ENGLISH_GERMAN = 'ENGLISH_GERMAN',
    GERMAN_ENGLISH = 'GERMAN_ENGLISH'
}

const ReservationConfirmationPrintout = () => {
    const {t} = useLocalize()
    const {tFor} = useTranslateForLanguage()
    const {reservationId, folioItemId} = useParams()
    const navigate = useNavigate()

    const {data: templatesData, isLoading: isLoadingTemplates} = usePrintTemplates()

    const reservationTemplates = useMemo(() => {
        return templatesData?.content?.filter(
            (template: PrintTemplate) => template.type === PrintTemplateType.RESERVATION_CONFIRMATION
        ) || []
    }, [templatesData])

    const [selectedTemplate, setSelectedTemplate] = useState<PrintTemplate | null>(null)
    const [languageMode, setLanguageMode] = useState<LanguageMode>(LanguageMode.ENGLISH)

    React.useEffect(() => {
        if (reservationTemplates.length > 0 && !selectedTemplate) {
            setSelectedTemplate(reservationTemplates[0])
        }
    }, [reservationTemplates, selectedTemplate])

    const reservationQuery = useQuery(['reservation', reservationId], async () => {
        if (reservationId) {
            return ReservationService.getReservationById(reservationId)
        }
        return null
    }, {
        enabled: !!reservationId,
    })

    const reservation = reservationQuery.data

    const profile = useProfile(reservation?.primaryProfileId ?? reservation?.profileIds?.[0])

    const folioItemsQuery = useQuery(['folioItems', folioItemId], async () => {
        if (folioItemId) {
            const items = await FolioItemService.getAllFolioItems({
                id: [folioItemId],
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: 'id', direction: 'ASC'}]
                },
            })
            return items.content || []
        }
        return []
    }, {
        enabled: !!folioItemId,
    })

    const folioItems = folioItemsQuery.data || []

    const getPrintTranslation = (key: string): string => {
        switch (languageMode) {
            case LanguageMode.ENGLISH:
            case LanguageMode.ENGLISH_GERMAN:
                return tFor('en')(key)
            case LanguageMode.GERMAN:
            case LanguageMode.GERMAN_ENGLISH:
                return tFor('de')(key)
            default:
                return tFor('en')(key)
        }
    }

    const lines: FolioPrintoutLine[] = folioItems.map((item: FolioItem) => {
        return {
            date: reservation?.expectedStartDate ? (item.inclusive ? '→ ' : '') + formatDateDMYHM(reservation?.expectedStartDate) : '-',
            duration: item.timeSpanStart && item.timeSpanEnd ?
                `${Math.round((new Date(item.timeSpanEnd).getTime() - new Date(item.timeSpanStart).getTime()) / (1000 * 60))} min` : '-',
            description: (item.description || '').substring(0, 100),
            amount: item.quantity ? item.quantity.toString() : '1',
            total: item.inclusive ? getPrintTranslation('Inclusive') : item.totalAmount?.amount ? `${item.totalAmount.amount.toFixed(2)} ${getSymbolForCurrency(item.totalAmount.currency, '')}` : '-',
            meta1: item.serviceInventoryCode || '',
        }
    })

    const getLocalizedContent = (localizedText: any) => {
        if (!localizedText) return {primary: '', secondary: ''}

        switch (languageMode) {
            case LanguageMode.ENGLISH:
                return {
                    primary: localizedText['en'] || Object.values(localizedText)[0] || '',
                    secondary: ''
                }
            case LanguageMode.GERMAN:
                return {
                    primary: localizedText['de'] || Object.values(localizedText)[0] || '',
                    secondary: ''
                }
            case LanguageMode.ENGLISH_GERMAN:
                return {
                    primary: localizedText['en'] || '',
                    secondary: localizedText['de'] || ''
                }
            case LanguageMode.GERMAN_ENGLISH:
                return {
                    primary: localizedText['de'] || '',
                    secondary: localizedText['en'] || ''
                }
            default:
                return {
                    primary: localizedText['en'] || Object.values(localizedText)[0] || '',
                    secondary: ''
                }
        }
    }

    const headerContent1 = getLocalizedContent(selectedTemplate?.headerContent1Localized)
    const headerContent2 = getLocalizedContent(selectedTemplate?.headerContent2Localized)
    const bottomContent1 = getLocalizedContent(selectedTemplate?.bottomContent1Localized)
    const bottomContent2 = getLocalizedContent(selectedTemplate?.bottomContent2Localized)

    const getBottomSectionContent = () => {
        const hasBottomContent = (bottomContent1.primary || bottomContent1.secondary) || (bottomContent2.primary || bottomContent2.secondary)

        if (hasBottomContent) {
            return (
                <>
                    {(bottomContent1.primary || bottomContent1.secondary) && (
                        <div style={{marginBottom: '4mm'}}>
                            {renderBilingualText(bottomContent1.primary, bottomContent1.secondary)}
                        </div>
                    )}
                    {(bottomContent2.primary || bottomContent2.secondary) && (
                        <div>
                            {renderBilingualText(bottomContent2.primary, bottomContent2.secondary)}
                        </div>
                    )}
                </>
            )
        }

        // Fallback content when template has no bottom content
        return (
            <>
                {t('Thank you for your reservation')}!<br/>
                {t('We look forward to your arrival')}.
            </>
        )
    }

    const getTableHeaders = () => {
        const getHeader = (key: string) => {
            switch (languageMode) {
                case LanguageMode.ENGLISH:
                    return {primary: tFor('en')(key), secondary: ''}
                case LanguageMode.GERMAN:
                    return {primary: tFor('de')(key), secondary: ''}
                case LanguageMode.ENGLISH_GERMAN:
                    return {primary: tFor('en')(key), secondary: tFor('de')(key)}
                case LanguageMode.GERMAN_ENGLISH:
                    return {primary: tFor('de')(key), secondary: tFor('en')(key)}
                default:
                    return {primary: tFor('en')(key), secondary: ''}
            }
        }

        return {
            date: getHeader('Date'),
            duration: getHeader('Duration'),
            description: getHeader('Description'),
            amount: getHeader('Amount'),
            info: getHeader('Info'),
            price: getHeader('Price')
        }
    }

    const tableHeaders = getTableHeaders()

    const languageModeOptions = [
        {value: LanguageMode.ENGLISH, label: t('English')},
        {value: LanguageMode.GERMAN, label: t('German')},
        {
            value: LanguageMode.ENGLISH_GERMAN,
            label: `${t('English')} / ${t('German')}`
        },
        {
            value: LanguageMode.GERMAN_ENGLISH,
            label: `${t('German')} / ${t('English')}`
        }
    ]

    const renderBilingualText = (primary: string, secondary: string) => {
        if (!primary && !secondary) return null
        if (!secondary) {
            return <div dangerouslySetInnerHTML={{__html: primary}}/>
        }
        return (
            <>
                <div dangerouslySetInnerHTML={{__html: primary}}/>
                <div
                    style={{
                        fontSize: '0.75em',
                        opacity: 0.7,
                        marginTop: '2mm'
                    }}
                    dangerouslySetInnerHTML={{__html: secondary}}
                />
            </>
        )
    }

    const renderTableHeaderCell = (header: {
        primary: string,
        secondary: string
    }, style: React.CSSProperties = {}) => {
        if (!header.secondary) {
            return <td style={style}>{header.primary}</td>
        }
        return (
            <td style={style}>
                <div>{header.primary}</div>
                <div style={{
                    fontSize: '0.75em',
                    opacity: 0.7
                }}>{header.secondary}</div>
            </td>
        )
    }

    if (isLoadingTemplates || reservationQuery.isLoading) {
        return (
            <ScreenWrapper>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px',
                    }}
                >
                    <CircularProgress size={60}/>
                </Box>
            </ScreenWrapper>
        )
    }



    //region UI
    return (
        <ScreenWrapper>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    width: '100%',
                }}
            >
                <Card
                    className={'hide-on-print'}
                    sx={{
                        width: '100%',
                        maxWidth: '800px',
                    }}
                >
                    <CardContent>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-end', flex: 1, minWidth: '200px'}}>
                                <Autocomplete
                                    id={'template-selector'}
                                    options={reservationTemplates}
                                    getOptionLabel={(option: PrintTemplate) => option.name || ''}
                                    value={selectedTemplate}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('Template')}
                                        />
                                    )}
                                    onChange={(e, value) => {
                                        setSelectedTemplate(value)
                                    }}
                                    sx={{flex: 1}}
                                    size={'small'}
                                />
                                {reservationTemplates.length === 0 && (
                                    <Button
                                        size={'small'}
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => navigate('/system/printTemplates')}
                                    >
                                        {t('Create')}
                                    </Button>
                                )}
                            </Box>

                            <Autocomplete
                                id={'language-mode-selector'}
                                options={languageModeOptions}
                                getOptionLabel={(option) => option.label}
                                value={languageModeOptions.find(opt => opt.value === languageMode) || languageModeOptions[0]}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('Language')}
                                    />
                                )}
                                onChange={(e, value) => {
                                    if (value) {
                                        setLanguageMode(value.value)
                                    }
                                }}
                                sx={{flex: 1, minWidth: '200px'}}
                                size={'small'}
                            />

                            <Box sx={{display: 'flex', gap: 1}}>
                                {/* Both print paths stay supported: this opens the new printout,
                                    the button beside it prints the legacy template. */}
                                <Button
                                    size={'small'}
                                    variant="outlined"
                                    component="a"
                                    href={`/print/printout/reservationConfirmation/${reservationId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('Printout (new)')}
                                </Button>
                                <Button
                                    size={'small'}
                                    variant="contained"
                                    color="success"
                                    onClick={() => window.print()}
                                    startIcon={<PrintIcon/>}
                                >
                                    {t('Print')}
                                </Button>

                                <Button
                                    size={'small'}
                                    variant="contained"
                                    color="error"
                                    onClick={() => window.close()}
                                    startIcon={<CloseIcon/>}
                                >
                                    {t('Close')}
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {!reservation ? (
                    <div>{t('Reservation not found')}</div>
                ) : (
                    <GenericPrintoutPreview
                        title={
                            headerContent1.secondary ? (
                                <>
                                    {headerContent1.primary || getPrintTranslation('Confirmation')}
                                    <div style={{
                                        fontSize: '0.75em',
                                        opacity: 0.7,
                                        marginTop: '2mm'
                                    }}>
                                        {headerContent1.secondary}
                                    </div>
                                </>
                            ) : (
                                headerContent1.primary || getPrintTranslation('Confirmation')
                            )
                        }
                        subtitle={
                            headerContent2.secondary ? (
                                <>
                                    {headerContent2.primary}
                                    <div style={{
                                        fontSize: '0.75em',
                                        opacity: 0.7,
                                        marginTop: '2mm'
                                    }}>
                                        {headerContent2.secondary}
                                    </div>
                                </>
                            ) : (
                                headerContent2.primary
                            )
                        }
                        firstName={profile?.firstName}
                        lastName={profile?.lastName}
                        roomNumber={profile?.currentStay?.roomId ?? profile?.activeStays?.[0]?.roomId}
                        headerLogo={selectedTemplate?.headerLogo}
                        showSignature={selectedTemplate?.showSignature}
                        signatureText={getPrintTranslation('Signature')}
                        showTip={selectedTemplate?.showTip}
                        tipText={getPrintTranslation('Tip amount')}
                        bottomSectionContent={getBottomSectionContent()}
                        tableHead={
                            <thead>
                            <tr>
                                {renderTableHeaderCell(tableHeaders.date, {
                                    color: '#666',
                                    paddingRight: '8px'
                                })}
                                {renderTableHeaderCell(tableHeaders.duration, {
                                    color: '#666',
                                    paddingRight: '8px'
                                })}
                                {renderTableHeaderCell(tableHeaders.description, {
                                    color: '#666',
                                    paddingRight: '8px'
                                })}
                                {renderTableHeaderCell(tableHeaders.amount, {
                                    color: '#666',
                                    paddingRight: '8px'
                                })}
                                {renderTableHeaderCell(tableHeaders.info, {
                                    color: '#666',
                                    paddingRight: '8px'
                                })}
                                {renderTableHeaderCell(tableHeaders.price, {color: '#666'})}
                            </tr>
                            </thead>
                        }
                        tableBody={
                            <tbody>
                            {
                                lines?.map((line: FolioPrintoutLine, index: number) => {
                                    return (
                                        <tr
                                            key={index}
                                            style={{
                                                height: '8mm',
                                            }}
                                        >
                                            <td style={{paddingRight: '8px'}}>{line.date}</td>
                                            <td style={{paddingRight: '8px'}}>{line.duration}</td>
                                            <td style={{paddingRight: '8px'}}>{line.description}</td>
                                            <td style={{paddingRight: '8px'}}>{line.amount || '-'}</td>
                                            <td style={{paddingRight: '8px'}}>{line.meta1 || '-'}</td>
                                            <td>{line.total}</td>
                                        </tr>
                                    )
                                })
                            }
                            </tbody>
                        }
                    />
                )}
            </Box>
        </ScreenWrapper>
    )
    //endregion UI
}

export default ReservationConfirmationPrintout